/**
 * B-408 — Weryfikacja CEPiK: wspólne typy, etykiety i drobne klocki dla zakładki
 * w Kierowcach, sekcji w Ustawieniach i okna alertu.
 */
import type { ElementType } from 'react';
import {
  ShieldCheck, ShieldX, CalendarClock, FileWarning, FileX2, SearchX, FileQuestion, WifiOff, Clock,
} from 'lucide-react';
import { apiClient } from '@/shared/lib/api';
import { cn } from '@/shared/lib/utils';
import { HEX, type Tone, TONE } from '@/shared/components/ListPageKit';

export type Bucket = 'lost' | 'expiring' | 'valid' | 'warn' | 'invalid' | 'notFound' | 'noBlankiet' | 'error' | 'never';

export interface CepikCategory { kategoria: string; dataWaznosci: string | null }

export interface CepikDriverRow {
  id: string;
  firstName: string;
  lastName: string;
  serviceNumber: string | null;
  depotCode: string | null;
  driverType: 'DRIVER' | 'MOTORMAN' | null;
  isActive: boolean;
  externalSource: string | null;
  licenseCategories: string[];
  licenseExpiry: string | null;
  cepikStatus: string | null;
  cepikStan: string | null;
  cepikReasons: string[] | null;
  cepikCategories: CepikCategory[] | null;
  cepikDocType: string | null;
  cepikDocExpiry: string | null;
  cepikNearestExpiry: string | null;
  cepikCheckedAt: string | null;
  cepikChangedAt: string | null;
  cepikAttemptAt: string | null;
  cepikLastError: string | null;
  hasDocument: boolean;
  docKind: 'PJ' | 'PT' | null;
  bucket: Bucket;
  onDutyToday: boolean;
  onDutyTomorrow: boolean;
  /** B-497 — dla „Nie znaleziono” / „Błędny numer”: od kiedy i czy kiedykolwiek znaleziony. */
  cepikProblem?: { since: string; everFound: boolean; lastFoundAt: string | null; count: number } | null;
  /** B-422 — niepotwierdzone alerty kierowcy („wymaga reakcji”); `firstId` = alert do otwarcia (krytyczny najpierw). */
  openAlerts?: { count: number; critical: number; firstId: string; kinds: string[] } | null;
  /** Uprawnienia z kartoteki GAiT bez numeru (np. „Pozwolenie” u motorniczych). */
  gaitPermits?: { name: string; expiresAt: string | null }[];
}

/** B-497 — jedno zdanie do listy: ostatnio znaleziono + od kiedy brak wyniku, albo „ani razu” (bezosobowo — kierowcy i kierowczynie). */
export const problemText = (p: NonNullable<CepikDriverRow['cepikProblem']>) => (p.everFound
  ? `ostatnio znaleziono ${fmtDate(p.lastFoundAt)}; od ${fmtDate(p.since)} brak wyniku`
  : `ani razu nie znaleziono — od ${fmtDate(p.since)}`);

/** Dzisiejsza data w czasie polskim (YYYY-MM-DD). */
export const todayPl = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Warsaw' }).format(new Date());

/** CEPiK przy ważnym dokumencie potrafi przysłać „Brak danych” jako powód — to nie jest powód. */
export const realReasons = (r: string[] | null | undefined) =>
  (r ?? []).map((s) => String(s).trim()).filter((s) => s && !/^brak danych$/i.test(s));

/** `string`, bo karta kierowcy trzyma rodzaj jako zwykły napis; poza DRIVER/MOTORMAN działa jak „nieznany”. */
export type WorkKind = string | null | undefined;

/**
 * Kategorie istotne dla pracy — lustro `workCategories` z backendu (cepik-classify.ts): kierowca autobusu — rodzina D,
 * motorniczy — wyłącznie kat. U (pozwolenie tramwajowe), nieznany rodzaj — wszystkie. Wygasła kategoria spoza tej listy
 * jest tylko informacją (nie czerwona, nie blokuje).
 */
export function workCategories(cats: CepikCategory[], kind: WorkKind): CepikCategory[] {
  if (kind === 'DRIVER') {
    const d = cats.filter((c) => /^D/i.test(c.kategoria));
    return d.length ? d : cats;
  }
  if (kind === 'MOTORMAN') return cats.filter((c) => c.kategoria.toUpperCase() === 'U');
  return cats;
}

/** Podpowiedź przy wygasłej kategorii, która nie dotyczy stanowiska. */
export const NOT_WORK_HINT = (kind: WorkKind) =>
  `Ta kategoria nie jest wymagana na stanowisku ${kind === 'MOTORMAN' ? 'motorniczego (liczy się pozwolenie tramwajowe, kat. U)' : 'kierowcy autobusu (liczy się kat. D)'} — nie blokuje.`;

/**
 * Jedno zdanie „dlaczego utrata” do listy i karty: przy zatrzymaniu/cofnięciu — powód
 * z CEPiK (`powodZmianyStanu`), przy wygaśnięciu — które kategorie i kiedy wygasły
 * (tylko te istotne dla stanowiska — `driverType`).
 * null, gdy status nie jest utratą.
 */
export function lossSummary(
  d: { cepikStatus: string | null; cepikCategories: CepikCategory[] | null; cepikReasons: string[] | null; driverType?: WorkKind },
  today = todayPl(),
): string | null {
  if (!d.cepikStatus || !LOSS.has(d.cepikStatus)) return null;
  if (d.cepikStatus === 'EXPIRED') {
    const byDate = new Map<string, string[]>();
    for (const c of workCategories(d.cepikCategories ?? [], d.driverType)) {
      if (c.dataWaznosci && c.dataWaznosci < today) byDate.set(c.dataWaznosci, [...(byDate.get(c.dataWaznosci) ?? []), c.kategoria]);
    }
    if (!byDate.size) return 'Dokument wygasł';
    return [...byDate.entries()].sort(([a], [b]) => (a < b ? 1 : -1))
      .map(([date, cats]) => `wygasła kat. ${cats.join(', ')} (${fmtDate(date)})`)
      .join('; ')
      .replace(/^w/, 'W');
  }
  const reasons = realReasons(d.cepikReasons);
  return `${STATUS_LABEL[d.cepikStatus]} — ${reasons.length ? `powód: ${reasons.join('; ')}` : 'CEPiK nie podał powodu'}`;
}

export interface CepikRun {
  id: string;
  trigger: 'MANUAL' | 'SCHEDULE';
  scopeLabel: string;
  status: 'QUEUED' | 'RUNNING' | 'DONE' | 'CANCELLED' | 'BREAKER' | 'ERROR';
  cursor: number;
  total: number;
  valid: number; lost: number; warn: number; notFound: number; noBlankiet: number; errors: number; changed: number;
  cancelRequested: boolean;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
  startedByName: string | null;
  /** B-418 — kiedy poszedł zbiorczy raport e-mailem (null = nie wysłano). */
  reportSentAt?: string | null;
}

export interface CepikOverview {
  configured: boolean;
  total: number;
  expiringDays: number;
  counts: Record<Bucket, number>;
  activeRun: CepikRun | null;
  lastRun: CepikRun | null;
  schedule: { enabled: boolean; frequency: string; next: string | null };
}

export interface CepikDuty { day: string; date: string; start: string | null; line: string | null; brigade: string | null; duty: string | null }

export interface CepikAlert {
  id: string;
  driverId: string;
  kind: string;
  severity: 'CRITICAL' | 'WARNING' | 'INFO';
  title: string;
  details: {
    driver?: { name: string; serviceNumber: string | null; depot: string | null; type: string | null };
    prevStatus?: string | null;
    status?: string;
    stan?: string | null;
    reasons?: string[];
    why?: string | null;
    categories?: CepikCategory[];
    docExpiry?: string | null;
    organ?: string | null;
    gait?: { categories: string[]; expiry: string | null } | null;
    duty?: CepikDuty[];
    mismatch?: string[];
    lostCategories?: string[];
    daysLeft?: number;
  } | null;
  createdAt: string;
  acknowledgedAt: string | null;
  acknowledgedByName: string | null;
  note: string | null;
  /** B-418 — wysyłki alertu e-mailem z okna / karty. */
  emails?: Array<{ at: string; byId: string; byName: string; to: Array<{ id: string; name: string; email: string; external?: boolean }>; note: string | null; sent: number }> | null;
}

export interface CepikCheck {
  id: string;
  checkedAt: string;
  trigger: 'SINGLE' | 'BULK' | 'SCHEDULE';
  outcome: 'FOUND' | 'NOT_FOUND' | 'NO_BLANKIET' | 'ERROR';
  status: string | null;
  stan: string | null;
  reasons: string[] | null;
  categories: CepikCategory[] | null;
  docExpiry: string | null;
  organ: string | null;
  docKind: 'PJ' | 'PT' | null;
  source: string | null;
  message: string | null;
  changed: boolean;
  userName: string | null;
}

export const BUCKET: Record<Bucket, { label: string; short: string; tone: Tone; hex: string; icon: ElementType }> = {
  // B-557: wygaśnięcie to nie „utrata” — zbiorcza nazwa grupy wygasłe + zatrzymane + cofnięte.
  lost:       { label: 'Brak ważnych uprawnień', short: 'Brak ważnych', tone: 'red',   hex: HEX.red,     icon: ShieldX },
  expiring:   { label: 'Wygasa wkrótce',     short: 'Wygasa',          tone: 'amber',   hex: HEX.amber,   icon: CalendarClock },
  valid:      { label: 'Ważne',              short: 'Ważne',           tone: 'emerald', hex: HEX.emerald, icon: ShieldCheck },
  warn:       { label: 'Do wyjaśnienia',     short: 'Do wyjaśnienia',  tone: 'orange',  hex: HEX.orange,  icon: FileWarning },
  // B-495: CEPiK odrzucił samo zapytanie (zły format numeru) — dane do poprawy, nie „nie ma w rejestrze”.
  invalid:    { label: 'Błędny numer dokumentu', short: 'Błędny numer', tone: 'purple', hex: HEX.purple, icon: FileX2 },
  notFound:   { label: 'Nie znaleziono',     short: 'Nie znaleziono',  tone: 'slate',   hex: HEX.slate,   icon: SearchX },
  noBlankiet: { label: 'Brak numeru druku',  short: 'Brak druku',      tone: 'violet',  hex: HEX.violet,  icon: FileQuestion },
  error:      { label: 'Błąd sprawdzenia',   short: 'Błąd',            tone: 'rose',    hex: HEX.rose,    icon: WifiOff },
  never:      { label: 'Nie sprawdzano',     short: 'Nie sprawdzano',  tone: 'sky',     hex: HEX.sky,     icon: Clock },
};
export const BUCKET_ORDER: Bucket[] = ['lost', 'expiring', 'warn', 'invalid', 'error', 'notFound', 'noBlankiet', 'never', 'valid'];

export const STATUS_LABEL: Record<string, string> = {
  VALID: 'Ważne',
  SUSPENDED: 'Zatrzymane',
  REVOKED: 'Cofnięte',
  EXPIRED: 'Wygasłe',
  REPLACED: 'Dokument wymieniony',
  OTHER: 'Inny stan dokumentu',
  NOT_FOUND: 'Nie znaleziono',
  NO_BLANKIET: 'Brak numeru druku',
  INVALID_INPUT: 'Błędny numer dokumentu',
};
export const LOSS = new Set(['SUSPENDED', 'REVOKED', 'EXPIRED']);

/** B-557 — znacznik na liście Kierowców wg rodzaju braku uprawnień (wcześniej zawsze „utrata”). */
export const LOSS_CHIP: Record<string, string> = { EXPIRED: 'wygasło', SUSPENDED: 'zatrzymane', REVOKED: 'cofnięte' };

/** B-557 — nagłówek okna alertu: nazywa rodzaj braku uprawnień, nie „utratę” (wygasłe to nie utrata). */
export function alertHeading(a: { kind: string; details?: { status?: string } | null }): string {
  const byStatus: Record<string, string> = {
    EXPIRED: 'Uprawnienia do kierowania wygasły',
    SUSPENDED: 'Uprawnienia do kierowania zatrzymane',
    REVOKED: 'Uprawnienia do kierowania cofnięte',
  };
  return (a.kind === 'LOSS' && a.details?.status && byStatus[a.details.status]) || 'Brak ważnych uprawnień do kierowania';
}

export const TRIGGER_LABEL: Record<string, string> = {
  SINGLE: 'pojedynczo', BULK: 'przebieg ręczny', SCHEDULE: 'harmonogram', MANUAL: 'ręcznie',
};

export const RUN_STATUS: Record<CepikRun['status'], { label: string; cls: string }> = {
  QUEUED:    { label: 'W kolejce',   cls: 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300' },
  RUNNING:   { label: 'W trakcie',   cls: 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300' },
  DONE:      { label: 'Zakończony',  cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' },
  CANCELLED: { label: 'Przerwany',   cls: 'bg-slate-100 text-slate-700 dark:bg-slate-500/15 dark:text-slate-300' },
  BREAKER:   { label: 'Wstrzymany — błędy CEPiK', cls: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300' },
  ERROR:     { label: 'Błąd',        cls: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300' },
};

export const fmtDateTime = (s?: string | null) => {
  if (!s) return '—';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' });
};
export const fmtDate = (s?: string | null) => (s ? `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}` : '—');
/** Ważność kategorii z CEPiK: brak daty = „bezterminowo” (tak pokazuje gov.pl), nie „nie wiadomo”. */
export const catValidity = (d: string | null | undefined, expired = false) =>
  !d ? 'bezterminowo' : `${expired ? 'wygasła ' : 'do '}${fmtDate(d)}`;
export const fmtTime = (s?: string | null) =>
  s ? new Date(s).toLocaleTimeString('pl-PL', { timeZone: 'Europe/Warsaw', hour: '2-digit', minute: '2-digit' }) : '';

/** Dni od dziś (Polska) do daty YYYY-MM-DD. */
export function daysLeft(ymd: string): number {
  const today = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Warsaw' }).format(new Date());
  const a = Date.UTC(+ymd.slice(0, 4), +ymd.slice(5, 7) - 1, +ymd.slice(8, 10));
  const b = Date.UTC(+today.slice(0, 4), +today.slice(5, 7) - 1, +today.slice(8, 10));
  return Math.round((a - b) / 86_400_000);
}

export function relDays(n: number): string {
  if (n === 0) return 'dziś';
  if (n === 1) return 'jutro';
  if (n < 0) return `${-n} dni temu`;
  return `za ${n} dni`;
}

export function BucketPill({ bucket, className }: { bucket: Bucket; className?: string }) {
  const b = BUCKET[bucket];
  const Icon = b.icon;
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold', TONE[b.tone].count, className)}>
      <Icon className="h-3 w-3" />{b.short}
    </span>
  );
}

/** Pobranie pliku z API (z tokenem) — raport PDF, bez otwierania nowej karty. */
export async function downloadFromApi(url: string, filename: string) {
  const res = await apiClient.get(url, { responseType: 'blob' });
  const href = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 5000);
}

export function downloadCsv(filename: string, header: string[], rows: (string | number | null | undefined)[][]) {
  const esc = (v: unknown) => {
    const s = v == null ? '' : String(v);
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = [header, ...rows].map((r) => r.map(esc).join(';')).join('\r\n');
  const blob = new Blob(['﻿' + body], { type: 'text/csv;charset=utf-8' });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 5000);
}

export const errMsg = (e: any, fallback: string) =>
  e?.response?.data?.message ?? e?.message ?? fallback;
